import { useEffect, useRef } from 'react';
import { focusManager } from '@tanstack/react-query';
import type { GlobalRefreshState } from './useGlobalRefresh';

/**
 * כמה זמן צריך לעבור מאז הנתונים הישנים שעל המסך כדי שחזרה ללשונית תרענן (HAT-1237).
 *
 * חצי דקה. מי שעבר לנדרים פלוס, ביצע שם משהו וחזר, שוהה שם הרבה יותר מזה
 * ויקבל נתונים טריים. מי שמקפץ בין האתר לאקסל כל כמה שניות לא יפעיל משיכה
 * בכל קפיצה - וזה לא חיסכון תיאורטי: הקריאה לתנועות מפעילה בשרת גם קריאה
 * מקבילה למערכת השנייה (ר' ApiTriggerService), כך שכל חזרה עלתה פעמיים.
 */
export const FOCUS_REFRESH_MIN_GAP_MS = 30000;

/**
 * האם חזרה ללשונית בנקודה הזו מצדיקה רענון.
 *
 * `updatedAt` הוא הישן שבנתוני המסך (ר' useGlobalRefresh), ולכן גם התנאי
 * "טרם נמשך הכול" מגולם בו: אפס אומר שמשהו על המסך עדיין בדרך, ואז אין מה
 * לרענן - המשיכה כבר באוויר.
 */
export const shouldRefreshOnFocus = (
  { updatedAt, isRefreshing }: Pick<GlobalRefreshState, 'updatedAt' | 'isRefreshing'>,
  now: number = Date.now()
): boolean => !isRefreshing && updatedAt > 0 && now - updatedAt >= FOCUS_REFRESH_MIN_GAP_MS;

/**
 * רענון בחזרה ללשונית — אותו רענון בדיוק שהכפתור עושה (HAT-1315).
 *
 * ⚠️ לכן זה כאן, ולא `refetchOnWindowFocus` על שאילתה בודדת.
 *
 * עד HAT-1315 כל מסך הצהיר פעמיים: `meta.refreshable` קבע מה הכפתור מושך,
 * ו-`refetchOnWindowFocus` קבע מה נמשך אוטומטית - ושתי ההצהרות יצאו מסנכרון.
 * בלוח התנועות רק הלוח עצמו נמשך בחזרה ללשונית, בעוד היתרה שבפס העליון -
 * שגם היא `refreshable` - נשארה כפי שהייתה. החותמת היא הישן שבנתונים, ולכן
 * היא נשארה תקועה על שעת הכניסה לאתר, וזה מה שדווח: "נהיה רענון אוטומטי
 * והשעה לא משתנה, ברענון ידני כן".
 *
 * החותמת לא שיקרה - הרענון האוטומטי באמת היה חלקי, והיתרה באמת לא התרעננה.
 * לכן התיקון אינו בחישוב החותמת אלא כאן: הצהרה אחת, `meta.refreshable`,
 * קובעת גם מה הכפתור מושך וגם מה נמשך בחזרה ללשונית. "אוטומטי = ידני" נכון
 * מעצם המבנה, ולא מכך שמי שמוסיף מסך זכר להצהיר פעמיים.
 *
 * ⚠️ נרכב פעם אחת בלבד, ב-FundBalanceBar שמעל כל מסך. שני מאזינים לפוקוס היו
 * מפעילים שני רענונים על אותה חזרה ללשונית.
 */
export const useFocusRefresh = (state: GlobalRefreshState): void => {
  /*
   * המצב נקרא מ-ref ולא נסגר בתוך המאזין: הרישום צריך לקרות פעם אחת, והמאזין
   * צריך את המצב של רגע החזרה ללשונית - לא זה שהיה כשהוא נרשם.
   */
  const latest = useRef(state);
  useEffect(() => {
    latest.current = state;
  });

  useEffect(
    () =>
      focusManager.subscribe((focused) => {
        // גם עזיבת הלשונית מגיעה לכאן, ועליה אין מה לעשות.
        if (!focused) return;
        if (!shouldRefreshOnFocus(latest.current)) return;
        void latest.current.refresh();
      }),
    []
  );
};

export default useFocusRefresh;
